import axios from 'axios';
import { useContext, useEffect, useState, useCallback } from 'react';
import { Container, Row, Table, Form, Card, Nav } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import { FaPlus } from 'react-icons/fa';
import SuccessModal from '../components/modals/SuccessModal';
import { useNotificationModal } from '../hooks/useNotificationModal';

import '../styles/WarehouseGRN.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function WarehouseGRN() {
  const { userData } = useContext(AuthContext);
  const company_code = userData?.company_code;
  const navigate = useNavigate();

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showError,
    hideSuccess,
    hideError
  } = useNotificationModal();

  // Purchase Orders Table State 
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [, setPoSuppliers] = useState([]);
  const [poLoading, setPoLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  // Per-column filters for purchase orders table (client-side)
  const [poColumnFilter, setPoColumnFilter] = useState({
    po_number: '',
    supplier: '',
    total_quantity: '',
    total_styles: '',
    total_cost: '',
    tolerance_limit: '',
    latest_grn_date: ''
  });

  // Fetch purchase orders
  const fetchPurchaseOrders = useCallback(async () => {
    try {
      setPoLoading(true);
      const params = { 
        company_code,
        po_type: 'direct', 
        // ...poFilter,
        ...(activeTab !== 'all' && { status: activeTab }) // Only add status filter if not 'all'
      };
      const response = await axios.get(`${BASE_URL}/api/admin/grn/purchase-orders-with-status`, { params });
      setPurchaseOrders(response.data.purchase_orders || []);
    } catch (error) {
      showError(error.response?.data?.message || error.message, 'Error');
    } finally {
      setPoLoading(false);
    }
  }, [company_code, activeTab, showError]);

  // Fetch all PO numbers for dropdown (independent of filters)
  const fetchAllPONumbers = useCallback(async () => {
    if (!company_code) return;
    
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/grn/purchase-orders-with-status`, {
        params: { company_code } // No filters, get all POs
      });
      if (response.data.purchase_orders?.length > 0) {
        const poNumbers = response.data.purchase_orders.map(po => po.po_number);
        setAllPONumbers([...new Set(poNumbers)]); // Remove duplicates
      } else {
        setAllPONumbers([]);
      }
    } catch (error) {
      console.error('Error fetching PO numbers:', error);
      setAllPONumbers([]);
    }
  }, [company_code]); // Add activeTab to dependencies

  // Fetch suppliers for PO filter
  const fetchPoSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/suppliers/get-suppliers`, {
        params: { company_code }
      });
      setPoSuppliers(response.data.suppliers || []);
    } catch (error) {
      // ignore
    }
  }, [company_code]);

  useEffect(() => {
    if (company_code) {
      fetchPurchaseOrders();
      fetchPoSuppliers();
    }
  }, [company_code, fetchPurchaseOrders, fetchPoSuppliers]);

  // Separate useEffect for fetching PO numbers only once
  useEffect(() => {
    if (company_code) {
      fetchAllPONumbers();
    }
  }, [company_code]); 

  // PO Number dropdown state
  const [, setAllPONumbers] = useState([]);

  // Handle PO select - navigate to add GRN page
  const handleSelectPO = (po_number) => {
    navigate(`/warehouse/add-grn/${po_number}`);
  };

  // Helper to test if a value matches a filter
  const matches = (value, filter) => {
    if (!filter) return true;
    const val = (value === null || value === undefined) ? '' : String(value);
    return val.toLowerCase().includes(filter.toLowerCase());
  };

  return (
    <Container className="fluid-container-whgrn">
      <Row className="whgrn-row">
          <Card>
            <Card.Body>
              <div className="purchase-orders-header">
                <h1>Add GRN to Purchase Orders</h1>
              </div>
              
              {/* Status Tabs */}
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link 
                    active={activeTab === 'pending'} 
                    onClick={() => !poLoading && setActiveTab('pending')}
                    style={{ cursor: poLoading ? 'not-allowed' : 'pointer', opacity: poLoading ? 0.6 : 1 }}
                  >
                    Pending
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    active={activeTab === 'partial'} 
                    onClick={() => !poLoading && setActiveTab('partial')}
                    style={{ cursor: poLoading ? 'not-allowed' : 'pointer', opacity: poLoading ? 0.6 : 1 }}
                  >
                    Partial
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    active={activeTab === 'completed'} 
                    onClick={() => !poLoading && setActiveTab('completed')}
                    style={{ cursor: poLoading ? 'not-allowed' : 'pointer', opacity: poLoading ? 0.6 : 1 }}
                  >
                    Completed
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    active={activeTab === 'all'} 
                    onClick={() => !poLoading && setActiveTab('all')}
                    style={{ cursor: poLoading ? 'not-allowed' : 'pointer', opacity: poLoading ? 0.6 : 1 }}
                  >
                    All
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {/* Table */}
              {poLoading ? (
                <div className="text-center py-4">
                  <Spinner text="Loading purchase orders..." />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover className='grn-po-table'>
                    <thead>
                      <tr>
                        <th>PO Number</th>
                        <th>Supplier</th>
                        <th>Total Quantity</th>
                        <th>Total Products</th>
                        <th>Total Cost</th>
                        <th>Tolerance Limit (%)</th>
                        {(activeTab === 'pending' || activeTab === 'partial') && <th>Add to GRN</th>}
                        {(activeTab === 'partial' || activeTab === 'completed') && <th>Latest GRN Date</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Column-level search inputs - first row in tbody */}
                      <tr className="table-filters-row">
                        <td>
                          <Form.Control size="sm" type="text" placeholder="Search PO" value={poColumnFilter.po_number} onChange={e => setPoColumnFilter({ ...poColumnFilter, po_number: e.target.value })} />
                        </td>
                        <td>
                          <Form.Control size="sm" type="text" placeholder="Search Supplier" value={poColumnFilter.supplier} onChange={e => setPoColumnFilter({ ...poColumnFilter, supplier: e.target.value })} />
                        </td>
                        <td>
                          <Form.Control size="sm" type="text" placeholder="Qty" value={poColumnFilter.total_quantity} onChange={e => setPoColumnFilter({ ...poColumnFilter, total_quantity: e.target.value })} />
                        </td>
                        <td>
                          <Form.Control size="sm" type="text" placeholder="Products" value={poColumnFilter.total_styles} onChange={e => setPoColumnFilter({ ...poColumnFilter, total_styles: e.target.value })} />
                        </td>
                        <td>
                          <Form.Control size="sm" type="text" placeholder="Cost" value={poColumnFilter.total_cost} onChange={e => setPoColumnFilter({ ...poColumnFilter, total_cost: e.target.value })} />
                        </td>
                        <td>
                          <Form.Control size="sm" type="text" placeholder="Tolerance" value={poColumnFilter.tolerance_limit} onChange={e => setPoColumnFilter({ ...poColumnFilter, tolerance_limit: e.target.value })} />
                        </td>
                        {(activeTab === 'pending' || activeTab === 'partial') && <td></td>}
                        {(activeTab === 'partial' || activeTab === 'completed') && (
                          <td>
                            <Form.Control size="sm" type="text" placeholder="Latest GRN" value={poColumnFilter.latest_grn_date} onChange={e => setPoColumnFilter({ ...poColumnFilter, latest_grn_date: e.target.value })} />
                          </td>
                        )}
                      </tr>

                      {/* Filtered data rows */}
                      {(() => {
                        // Calculate correct column count based on active tab
                        let colsCount = 6; // Base columns: PO, Supplier, Qty, Products, Cost, Tolerance
                        if (activeTab === 'pending') {
                          colsCount = 7; // Add to GRN column
                        } else if (activeTab === 'partial') {
                          colsCount = 8; // Add to GRN + Latest GRN Date columns
                        } else if (activeTab === 'completed') {
                          colsCount = 7; // Latest GRN Date column
                        } else if (activeTab === 'all') {
                          colsCount = 8; // Show both columns for 'all'
                        }
                        
                        const filtered = purchaseOrders.filter(po => {
                          if (!matches(po.po_number, poColumnFilter.po_number)) return false;
                          if (!matches(po.supplier_name || po.supplier, poColumnFilter.supplier)) return false;
                          if (!matches(po.total_quantity || 0, poColumnFilter.total_quantity)) return false;
                          if (!matches(po.total_styles || 0, poColumnFilter.total_styles)) return false;
                          if (!matches((po.total_cost !== undefined ? parseFloat(po.total_cost).toFixed(2) : '0'), poColumnFilter.total_cost)) return false;
                          if (!matches((po.tolerance_limit !== undefined ? String(po.tolerance_limit) : '0'), poColumnFilter.tolerance_limit)) return false;
                          if ((activeTab === 'partial' || activeTab === 'completed') && !matches(po.latest_grn_date ? new Date(po.latest_grn_date).toLocaleDateString() : '-', poColumnFilter.latest_grn_date)) return false;
                          return true;
                        });

                        if (filtered.length === 0) {
                          return (
                            <tr>
                              <td colSpan={colsCount} className="text-center">No {activeTab === 'all' ? '' : activeTab} purchase orders found</td>
                            </tr>
                          );
                        }

                        return filtered.map(po => (
                          <tr key={po.po_number} style={{ cursor: 'pointer' }} onClick={() => handleSelectPO(po.po_number)}>
                            <td>{po.po_number}</td>
                            <td>{po.supplier_name || 'Unknown Supplier'}</td>
                            <td className='numbers-col'>{po.total_quantity || 0}</td>
                            <td className='numbers-col'>{po.total_styles || 0}</td>
                            <td className='numbers-col'>${parseFloat(po.total_cost || 0).toFixed(2)}</td>
                            <td className='numbers-col'>{po.tolerance_limit !== undefined ? po.tolerance_limit : 0}%</td>
                            {(activeTab === 'pending' || activeTab === 'partial') && <td><FaPlus className='add-to-grn-btn' title="Add to GRN" /></td>}
                            {(activeTab === 'partial' || activeTab === 'completed') && <td>{po.latest_grn_date ? new Date(po.latest_grn_date).toLocaleDateString() : '-'}</td>}
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
      </Row>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        message={successMessage}
        title={successTitle}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        message={errorMessage}
        title={errorTitle}
        variant="error"
      />
    </Container>
  );
}
